<template>
  <v-dialog v-model="dialog" max-width="1600px">
    <v-card class="fontNotoSans400">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ year }}년 희망이음 예산 업로드</span>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="downloadExcel" :loading="loading">
            <v-icon left>mdi-download</v-icon>
            엑셀 다운로드
          </v-btn>
          <v-btn icon="mdi-close" variant="text" @click="closeModal"></v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="hopeData"
          :items-per-page="-1"
          class="elevation-1"
          :loading="loading"
          density="compact"
        >
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.budget_amount="{ item }">
            <div class="text-right">{{ formatNumber(item.budget_amount || 0) }}</div>
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.calculation="{ item }">
            <div class="text-left" style="white-space: pre-wrap; max-width: 400px;">{{ item.calculation }}</div>
          </template>
        </v-data-table>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="downloadExcel" :loading="loading">
          <v-icon left>mdi-download</v-icon>
          엑셀 다운로드
        </v-btn>
        <v-btn color="grey" @click="closeModal">닫기</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const store = useStore();

// 예산 데이터 인터페이스
interface BudgetData {
  fc_trcode: string;
  fc_scode: string;
  fc_sname?: string;
  fc_funding?: string;
  fc_hope_scode?: string;  // 희망이음 계정코드 (DB에서 가져옴)
  fc_funding_name?: string;
  description?: string;
  unit_price?: string;
  count?: string;
  months?: string;
  hb_pay_rel?: string;
  isSalaryItem?: boolean;
  isLinkedItem?: boolean;
  isRevenuePairItem?: boolean;
}

// 희망이음 업로드용 데이터 인터페이스
interface HopeItem {
  biz_code: string;
  biz_name: string;
  account_code: string;
  account_name: string;
  fund_source_code: string;
  fund_source: string;
  budget_amount: number;
  national_fund: string;
  city_fund: string;
  district_fund: string;
  calculation: string;
}

// Props
interface Props {
  modelValue: boolean;
  year: string;
  budgetData: BudgetData[];
  bizGubun?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  showSnackbar: [message: string, type: string];
}>();

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const loading = ref(false);

// 사업 유형에 따른 사업코드 및 사업명
const getBizInfo = (bizGubun: string | undefined) => {
  switch (bizGubun) {
    case 'juga': return { code: '01', name: '주야간보호' };
    case 'danggi': return { code: '01', name: '단기보호' };
    case 'yoyang': return { code: '01', name: '방문요양' };
    case 'mokuk': return { code: '01', name: '방문목욕' };
    case 'ganho': return { code: '01', name: '방문간호' };
    case 'bokji': return { code: '01', name: '복지용구' };
    default: return { code: '01', name: '방문요양' };
  }
};

// 희망이음 계정코드 가져오기 (DB의 fc_hope_scode만 사용)
const getHopeAccountCode = (item: BudgetData): string => {
  // fc_hope_scode만 사용 (없으면 빈 문자열 반환하여 스킵 처리)
  return item.fc_hope_scode || '';
};

// 자금원천코드 가져오기 (DB의 fc_funding 사용)
const getFundSourceCode = (item: BudgetData): string => {
  // fc_funding이 있으면 사용, 없으면 기본값 '06' (수익사업)
  return item.fc_funding || '06';
};

// 숫자 파싱 (콤마 제거)
const parseNumber = (value: string | number | undefined): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  return Number(value.toString().replace(/,/g, '')) || 0;
};

// 산출근거 생성
const generateCalculation = (item: BudgetData): string => {
  const description = item.description || item.fc_sname || '';
  const unitPrice = parseNumber(item.unit_price);
  const count = parseNumber(item.count);
  const months = parseNumber(item.months);
  const amount = unitPrice * count * months;
  
  if (unitPrice === 0 && count === 0 && months === 0) {
    return '';
  }
  
  const formattedUnitPrice = unitPrice.toLocaleString();
  const formattedAmount = amount.toLocaleString();
  
  return `${description} : ${formattedUnitPrice} x ${count} x ${months} = ${formattedAmount}`;
};

// budgetData에서 hopeData 생성
const hopeData = computed<HopeItem[]>(() => {
  const items: HopeItem[] = [];
  const bizInfo = getBizInfo(props.bizGubun);
  
  // fc_hope_scode별로 그룹화하여 산출근거 합치기
  const groupedByCode = new Map<string, { items: BudgetData[], totalAmount: number, calculations: string[] }>();
  
  props.budgetData.forEach(item => {
    if (!item.fc_scode) return;
    
    const accountCode = getHopeAccountCode(item);
    if (!accountCode) return; // 희망이음 코드가 없으면 스킵
    
    const key = `${item.fc_trcode}-${accountCode}`;
    
    if (!groupedByCode.has(key)) {
      groupedByCode.set(key, { items: [], totalAmount: 0, calculations: [] });
    }
    
    const group = groupedByCode.get(key)!;
    group.items.push(item);
    
    const unitPrice = parseNumber(item.unit_price);
    const count = parseNumber(item.count);
    const months = parseNumber(item.months);
    const amount = unitPrice * count * months;
    
    group.totalAmount += amount;
    
    const calc = generateCalculation(item);
    if (calc) {
      group.calculations.push(calc);
    }
  });
  
  // 그룹화된 데이터를 희망이음 형식으로 변환 (예산액이 있는 것만)
  groupedByCode.forEach((group) => {
    // 예산액이 0이면 스킵
    if (group.totalAmount <= 0) return;
    
    const firstItem = group.items[0];
    const accountCode = getHopeAccountCode(firstItem);
    
    items.push({
      biz_code: bizInfo.code,
      biz_name: bizInfo.name,
      account_code: accountCode,
      account_name: firstItem.fc_sname || '',
      fund_source_code: getFundSourceCode(firstItem),
      fund_source: firstItem.fc_funding_name || '수익사업',
      budget_amount: group.totalAmount,
      national_fund: '',
      city_fund: '',
      district_fund: '',
      calculation: group.calculations.join('\n'),
    });
  });
  
  // 계정코드 순으로 정렬
  items.sort((a, b) => a.account_code.localeCompare(b.account_code));
  
  return items;
});

const headers = [
  { title: '사업코드', key: 'biz_code', sortable: true, width: '80px' },
  { title: '사업명', key: 'biz_name', sortable: true, width: '100px' },
  { title: '계정코드(세목)', key: 'account_code', sortable: true, width: '120px' },
  { title: '계정명(세목)', key: 'account_name', sortable: true, width: '180px' },
  { title: '자금원천코드', key: 'fund_source_code', sortable: true, width: '100px' },
  { title: '자금원천', key: 'fund_source', sortable: true, width: '100px' },
  { title: '예산액', key: 'budget_amount', sortable: true, width: '120px' },
  { title: '국비', key: 'national_fund', sortable: false, width: '80px' },
  { title: '시도비', key: 'city_fund', sortable: false, width: '80px' },
  { title: '시군구비', key: 'district_fund', sortable: false, width: '80px' },
  { title: '산출근거', key: 'calculation', sortable: false, width: '400px' },
];

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('ko-KR').format(num);
};

const closeModal = () => {
  dialog.value = false;
};

const downloadExcel = async () => {
  try {
    loading.value = true;
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('예산업로드');
    
    const excelData = hopeData.value;
    
    if (!excelData || excelData.length === 0) {
      emit('showSnackbar', '다운로드할 예산 데이터가 없습니다.', 'warning');
      loading.value = false;
      return;
    }
    
    // 헤더 설정
    const headerRow = [
      '사업코드',
      '사업명',
      '계정코드(세목)',
      '계정명(세목)',
      '자금원천코드',
      '자금원천',
      '예산액',
      '국비',
      '시도비',
      '시군구비',
      '산출근거'
    ];
    worksheet.addRow(headerRow);
    
    // 헤더 스타일 적용
    const header = worksheet.getRow(1);
    header.font = { bold: true };
    header.alignment = { vertical: 'middle', horizontal: 'center' };
    header.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3D3D3' }
    };
    header.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    
    // 열 너비 설정
    worksheet.columns = [
      { width: 12 },  // 사업코드
      { width: 15 },  // 사업명
      { width: 15 },  // 계정코드(세목)
      { width: 30 },  // 계정명(세목)
      { width: 15 },  // 자금원천코드
      { width: 12 },  // 자금원천
      { width: 15 },  // 예산액
      { width: 12 },  // 국비
      { width: 12 },  // 시도비
      { width: 12 },  // 시군구비
      { width: 60 }   // 산출근거
    ];
    
    // 데이터 추가
    excelData.forEach((item) => {
      const row = worksheet.addRow([
        item.biz_code,
        item.biz_name,
        item.account_code,
        item.account_name,
        item.fund_source_code,
        item.fund_source,
        item.budget_amount,
        item.national_fund,
        item.city_fund,
        item.district_fund,
        item.calculation
      ]);
      
      // 셀 스타일 적용
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // 1~6, 8~10번째 열: 가운데 정렬
        if (colNumber <= 6 || (colNumber >= 8 && colNumber <= 10)) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        // 7번째 열 (예산액): 우측 정렬, 숫자 포맷
        else if (colNumber === 7) {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
        }
        // 11번째 열 (산출근거): 좌측 정렬, 줄바꿈
        else if (colNumber === 11) {
          cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        }
      });
    });
    
    // 파일 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const sisulName = store.state.user?.m_sisul_name || '시설';
    const fileName = `${sisulName}_${props.year}년_희망이음_예산업로드.xlsx`;
    saveAs(blob, fileName);
    
    emit('showSnackbar', '희망이음 예산업로드 엑셀 다운로드 완료', 'success');
  } catch (error) {
    console.error('엑셀 다운로드 중 오류:', error);
    emit('showSnackbar', '엑셀 다운로드 중 오류가 발생했습니다.', 'error');
  } finally {
    loading.value = false;
  }
};

// 외부에서 다이얼로그 열기
const open = () => {
  dialog.value = true;
};

defineExpose({
  open
});
</script>

